import { OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import { FieldType } from '../enums/field-type.enum';
import { AppHelper } from '../helpers/app.helper';
import { IQueryService } from '../interfaces/query.service';
import { Client } from '../models/clients/client.model';
import { TaxonomyField } from '../models/fields/taxonomy-field.model';
import { Notification } from '../models/notifications/notification.model';
import { Taxonomy } from '../models/taxonomies/taxonomy.model';
import { NotificationService } from './notification.service';
import { TaxonomyService } from './taxonomy.service';


export class TaxonomyProcessor implements OnInit, OnDestroy {
  private clientsLoadingSubject = new BehaviorSubject<boolean>(false);
  private taxonomiesLoadingSubject = new BehaviorSubject<boolean>(false);
  private loadingSubject = new BehaviorSubject<boolean>(false);

  isClientsLoading$ = this.clientsLoadingSubject.asObservable();
  isTaxonomiesLoading$ = this.taxonomiesLoadingSubject.asObservable();
  isLoading$ = this.loadingSubject.asObservable();

  isInSelectMode = true;
  isClientsChecked = true;
  isTaxonomiesChecked = false;

  clients: Client[];
  taxonomies: Taxonomy[];
  fields: TaxonomyField[];

  selectedClient: Client = null;
  selectedTaxonomy: Taxonomy = null;

  clientsError: string;
  taxonomiesError: string;
  error: string;

  currOperation: string;

  get isReady(): boolean {
    return this.selectedClient
      && this.selectedTaxonomy
      && (!this.fields || this.fields.length > 0)
      && !this.loadingSubject.value;
  }

  get tableExists(): boolean {
    return this.selectedTaxonomy?.id
      && this.taxonomyService.taxonomyExists(this.selectedTaxonomy.id);
  }

  constructor(
    private queryService: IQueryService,
    private taxonomyService: TaxonomyService,
    private appHelper: AppHelper,
    private notificationService: NotificationService,
    private taxonomiesToExclude: { clientId: number, taxonomyId: number; }[]) {
  }

  ngOnInit(): void {
    this.taxonomyService.onDeleting
      .subscribe(taxonomyId => {
        if (!this.isInSelectMode && taxonomyId === this.selectedTaxonomy.id) {
          this.currOperation = `Removing the taxonomy table from the sheet`;
          this.loadingSubject.next(true);
        }
      });

    this.taxonomyService.onDeleted
      .subscribe(taxonomyId => {
        if (!this.isInSelectMode && taxonomyId === this.selectedTaxonomy.id) {
          this.loadingSubject.next(false);
        }
      });
  }

  ngOnDestroy(): void {
    this.clientsLoadingSubject.complete();
    this.taxonomiesLoadingSubject.complete();
    this.loadingSubject.complete();
  }

  loadClients(): void {
    this.clientsLoadingSubject.next(true);
    this.resetClients();
    this.queryService
      .getClients()
      .pipe(
        finalize(() => this.clientsLoadingSubject.next(false)))
      .subscribe(
        clients => this.clients = clients,
        error => this.clientsError = this.appHelper.getErrorMessage(error));
  }

  loadTaxonomies(): void {
    this.taxonomiesLoadingSubject.next(true);
    this.resetTaxonomies();
    this.queryService
      .getTaxonomies(this.selectedClient.id)
      .pipe(
        map(taxonomies => {
          const taxonomyIds = this.taxonomiesToExclude
            .filter(t => t.clientId === this.selectedClient.id)
            .map(t => t.taxonomyId);

          return taxonomies.filter(t => !this.appHelper.includes(taxonomyIds, t.id));
        }),
        finalize(() => this.taxonomiesLoadingSubject.next(false)))
      .subscribe(
        taxonomies => this.taxonomies = taxonomies,
        error => this.taxonomiesError = this.appHelper.getErrorMessage(error));
  }

  private async loadFields(): Promise<void> {
    this.fields = null;
    this.currOperation = 'Loading a list of taxonomy fields';

    const fields = await this.queryService
      .getTaxonomyFields(this.selectedTaxonomy.id)
      .toPromise();

    for (const field of fields) {
      switch (field.field_type) {
        case FieldType.MDMData:
          this.currOperation = `Loading MDM drop-down items of the '${field.name}' field`;

          const mdmItemsCached = this.taxonomyService.mdmDropDownItemsExist(this.selectedTaxonomy.id, field.id);

          field.dropdown_items = mdmItemsCached
            ? this.taxonomyService.getMdmDropDownItems(this.selectedTaxonomy.id, field.id)
            : await this.queryService.getMdmDropDownItems(this.selectedTaxonomy.id, field.id).toPromise();

          if (mdmItemsCached) {
            for (const item of field.dropdown_items) {
              if (this.taxonomyService.childMdmDropDownItemsExist(this.selectedTaxonomy.id, field.id, item.id)) {
                item.childItems = this.taxonomyService.getChildMdmDropDownItems(this.selectedTaxonomy.id, field.id, item.id);
              }
            }
          }
          break;
        case FieldType.Taxonomy:
          this.currOperation = `Loading taxonomy values for the '${field.name}' field`;
          field.dropdown_items = this.taxonomyService.taxonomyValuesExist(field.taxonomy_id)
            ? this.taxonomyService.getTaxonomyValues(field.taxonomy_id)
            : await this.queryService.getTaxonomyValues(this.selectedClient.id, field.taxonomy_id).toPromise();
          break;
      }

      if (field.field_type === FieldType.Static && field.mandatory && !field.static_field_value) {
        throw new Error(`The '${field.name}' is static mandatory field, however it does not contain a value.`);
      } else if (field.dropdown_items?.length === 0 && field.mandatory) {
        throw new Error(`The '${field.name}' field is mandatory, however it does not contain any items.`);
      }
    }

    this.fields = fields;
  }

  private resetClients(): void {
    this.clients = null;
    this.clientsError = null;
    this.onClientSelected(null);
  }

  private resetTaxonomies(): void {
    this.taxonomies = null;
    this.taxonomiesError = null;
    this.onTaxonomySelected(null);
  }

  onClientSelected(client: Client): void {
    if (this.selectedClient != client &&
      !this.clientsLoadingSubject.value &&
      !this.taxonomiesLoadingSubject.value &&
      !this.loadingSubject.value) {
      this.resetTaxonomies();
      this.selectedClient = client;
    }
  }

  onTaxonomySelected(taxonomy: Taxonomy): void {
    if (this.selectedTaxonomy != taxonomy &&
      !this.clientsLoadingSubject.value &&
      !this.taxonomiesLoadingSubject.value &&
      !this.loadingSubject.value) {
      this.fields = null;
      this.error = null;
      this.selectedTaxonomy = taxonomy;
    }
  }

  onCheckedChanged(key: string): void {
    if (this.isInSelectMode) {
      if (key === 'clients' && this.isClientsChecked) {
        if (this.isTaxonomiesChecked) {
          this.isTaxonomiesChecked = false;
        }
      } else if (key === 'taxonomies' && this.isTaxonomiesChecked) {
        if (this.isClientsChecked) {
          this.isClientsChecked = false;
        }

        if (this.selectedClient && !this.taxonomies) {
          this.loadTaxonomies();
        }
      }
    }
  }

  async insertData(): Promise<void> {
    try {
      this.error = null;
      this.loadingSubject.next(true);

      if (!this.fields) {
        await this.loadFields();
      }

      if (this.fields.length > 0) {
        this.currOperation = `Adding the taxonomy table to the sheet`;
        await this.taxonomyService.insertDataAsync(this.selectedTaxonomy.id, this.fields);
        this.isInSelectMode = false;
      }
    } catch (error) {
      this.error = this.appHelper.getErrorMessage(error);
    } finally {
      this.loadingSubject.next(false);
    }
  }

  async submitValues(): Promise<void> {
    try {
      this.error = null;
      this.loadingSubject.next(true);

      if (!this.fields) {
        await this.loadFields();
      }

      if (this.fields.length > 0) {
        this.isInSelectMode = false;
        this.currOperation = `Submitting taxonomy values`;

        await this.taxonomyService.submitValuesAsync(this.selectedClient.id, this.selectedTaxonomy.id);

        this.notificationService.show(new Notification('Taxonomy has been submitted'));
      }
    } catch (error) {
      this.error = this.appHelper.getErrorMessage(error);
    } finally {
      this.loadingSubject.next(false);
    }
  }
}